import { Auth0Provider } from '@auth0/auth0-react';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import ReactDOM from 'react-dom/client';
import { App } from './app';
import { DemoAuthProvider, isDemoMode } from './auth/demo-auth-provider';
import { router } from './router';

import './index.css';

if (import.meta.env.MODE === 'production') {
  datadogRum.init({
    applicationId: import.meta.env.VITE_DATADOG_APP_ID,
    clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN,
    site: 'datadoghq.com',
    service: import.meta.env.VITE_APP_DOMAIN,
    env: import.meta.env.VITE_APP_ENV,
    version: import.meta.env.VITE_APP_VERSION,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'allow',
    beforeSend: (event) => {
      if (
        event.type === 'error' &&
        event.error.source === 'network' &&
        event.error.resource?.url?.endsWith('/assets/VERSION')
      ) {
        return false;
      }

      return true;
    },
  });

  datadogLogs.init({
    clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN,
    site: 'datadoghq.com',
    service: import.meta.env.VITE_APP_DOMAIN,
    env: import.meta.env.VITE_APP_ENV,
    version: import.meta.env.VITE_APP_VERSION,
    forwardConsoleLogs: ['log', 'warn', 'error'],
    sessionSampleRate: 100,
  });
}

const domNode = document.querySelector('#app-root') as Element;
const root = ReactDOM.createRoot(domNode);

const authProvider = isDemoMode() ? (
  <DemoAuthProvider>
    <App />
  </DemoAuthProvider>
) : (
  <Auth0Provider
    domain={import.meta.env.VITE_AUTH0_DOMAIN}
    clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
    cacheLocation="localstorage"
    cookieDomain=".liquorstore.tech"
    authorizationParams={{
      redirect_uri: window.location.origin,
      audience: import.meta.env.VITE_AUTH0_AUDIENCE,
    }}
    onRedirectCallback={(appState) => {
      // Use router.history to update the URL before RouterProvider renders.
      // This preserves deep-links (appState.returnTo) and avoids the
      // / → /stores/$storeId redirect chain. Safe to call here because
      // the isAuthProcessing gate in App ensures RouterProvider hasn't
      // mounted yet, so there are no history subscribers.
      router.history.push(appState?.returnTo || '/');
    }}
  >
    <App />
  </Auth0Provider>
);

root.render(authProvider);

function removeServiceWorker() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker
      .getRegistrations()
      .then((registrations) => {
        for (const registration of registrations) {
          console.log('Unregistering service worker: ', registration);
          registration.unregister();
          console.log('Refreshing page');
          window.location.reload();
        }
      })
      .catch((error) => {
        console.log('Service Worker unregistration failed: ', error);
      });
  }
}

removeServiceWorker();
